"use client";

import React, { useEffect, useRef, useState } from "react";
import { Search, Globe, Truck, Building2 } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const pillars = [{
  id: "01",
  title: "Find Suppliers",
  description: "Access a verified database of trusted manufacturing and service partners across regions.",
  icon: Search
}, {
  id: "02",
  title: "Discover Buyers",
  description: "Expand your market reach by connecting with international procurement teams and distributors.",
  icon: Globe
}, {
  id: "03",
  title: "Logistics",
  description: "Seamless supply chain solutions, freight services, and customs clearance for cross-border trade.",
  icon: Truck
}, {
  id: "04",
  title: "Market Entry",
  description: "Expert guidance on legal setup, regulatory compliance, and establishing operations abroad.",
  icon: Building2
}];
export default function CoreTradePillars() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="core-trade-pillars" className="bg-muted/30 py-20 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-serif tracking-tight">
            Core Trade Pillars
          </h2>
          <p className="text-lg text-muted-foreground">
            Simplifying cross-border business between Europe and India through our comprehensive platform designed for growth and connectivity.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-1000 ease-out delay-200 ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <div data-index={i} key={pillar.id} className="h-full">
                <Card className="group relative h-full overflow-hidden border-border bg-card transition-all duration-300 hover:border-secondary hover:shadow-md">
                  {}
                  <span className="pointer-events-none absolute -bottom-6 -right-4 z-0 select-none text-9xl font-bold text-muted-foreground/5 transition-colors duration-300 group-hover:text-secondary/5">
                    {pillar.id}
                  </span>

                  <CardHeader className="relative z-10 pb-4">
                    <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/5 text-primary transition-transform duration-300 group-hover:translate-x-2 group-hover:bg-secondary/10 group-hover:text-secondary">
                      <Icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <CardTitle className="text-xl font-semibold text-foreground">
                      {pillar.title}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="relative z-10">
                    <CardDescription className="text-base text-muted-foreground leading-relaxed">
                      {pillar.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}